import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Flame, BookOpen, Sparkles, Target, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useDashboard } from "@/contexts/DashboardContext";
import { QualityBadge } from "@/components/LanguagePicker";
import DailyVoiceCard from "@/components/DailyVoiceCard";
import LexiaIntroModal from "@/components/LexiaIntroModal";

export default function Dashboard() {
    const { data, loading, refresh } = useDashboard();
    const [error, setError] = useState(null);
    const [introOpen, setIntroOpen] = useState(false);

    useEffect(() => {
        refresh().catch((e) => {
            setError(formatApiError(e));
            toast.error(formatApiError(e));
        });
    }, [refresh]);

    // Phase 10 — first-run welcome sequence. Open once the profile is loaded
    // if `lexia_intro_seen` is not truthy. The modal itself persists the flag.
    useEffect(() => {
        if (!data?.profile) return;
        if (!data.profile.lexia_intro_seen) {
            // Slight delay so the dashboard renders behind the modal first.
            const t = setTimeout(() => setIntroOpen(true), 700);
            return () => clearTimeout(t);
        }
    }, [data?.profile?.lexia_intro_seen]);

    if (error) {
        return <div className="p-10 text-sm text-terracotta">{error}</div>;
    }

    if (!data) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
            </div>
        );
    }

    const { user, profile, language, program_summary, stats, active_lesson } = data;
    const greetingHour = new Date().getHours();
    const greeting =
        greetingHour < 6
            ? "Encore debout"
            : greetingHour < 12
              ? "Bonjour"
              : greetingHour < 18
                ? "Bonne après-midi"
                : "Bonne soirée";

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="dashboard-page">
            {introOpen && (
                <LexiaIntroModal
                    voice={profile?.voice || "shimmer"}
                    onClose={() => { setIntroOpen(false); refresh?.(); }}
                />
            )}
            {/* Hero */}
            <section className="mb-10">
                <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3" data-testid="dashboard-eyebrow">
                    Tableau de bord
                </p>
                <h1
                    className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight"
                    data-testid="dashboard-greeting"
                >
                    {greeting}, <span className="italic text-terracotta">{user.first_name}</span>.
                </h1>
                <p className="mt-3 text-base text-inkSoft max-w-xl">
                    Un pas à la fois — vous avancez sur{" "}
                    <span className="text-ink font-medium">
                        {language?.native || "votre langue"}
                    </span>
                    {profile?.variant ? ` (${profile.variant})` : ""} ·{" "}
                    <span className="font-mono text-xs bg-paperAlt border border-lexiBorder rounded-full px-2 py-0.5">
                        {profile?.level}
                    </span>
                </p>
            </section>

            {/* Daily Voice Question — Phase 8 */}
            <DailyVoiceCard />

            {/* Program strip */}
            {program_summary?.exists && (
                <section
                    className="mb-10 bg-white border border-lexiBorder rounded-2xl p-6 md:p-8 flex flex-wrap items-center justify-between gap-6"
                    data-testid="dashboard-program-strip"
                >
                    <div className="min-w-0">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">
                            Jour {program_summary.current_day} / {program_summary.length_days} · {program_summary.current_day_theme?.cefr}
                        </p>
                        <h2
                            className="font-serif text-2xl md:text-3xl leading-tight mt-1 truncate"
                            data-testid="dashboard-today-theme"
                        >
                            {program_summary.current_day_theme?.theme_fr}
                        </h2>
                        <p className="text-sm text-inkSoft mt-1">
                            {program_summary.current_day_theme?.skill_focus}
                        </p>
                    </div>
                    <Link
                        to="/lesson"
                        data-testid="start-lesson-cta"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                    >
                        <BookOpen className="w-4 h-4" />
                        {active_lesson ? "Reprendre la leçon" : "Commencer la leçon du jour"}
                    </Link>
                </section>
            )}

            {/* Reviews CTA */}
            {stats.reviews_due > 0 && (
                <section
                    className="mb-10 bg-terracotta text-white rounded-2xl p-6 md:p-8 flex flex-wrap items-center justify-between gap-6"
                    data-testid="dashboard-reviews-strip"
                >
                    <div>
                        <p className="text-[11px] tracking-[0.24em] uppercase text-white/70">
                            Révisions
                        </p>
                        <h3 className="font-serif text-2xl md:text-3xl leading-tight mt-1">
                            {stats.reviews_due} mot{stats.reviews_due > 1 ? "s" : ""} à revoir
                        </h3>
                        <p className="text-sm text-white/85 mt-1">
                            Une petite session suffit pour ancrer les fragiles.
                        </p>
                    </div>
                    <Link
                        to="/reviews"
                        data-testid="start-reviews-cta"
                        className="inline-flex items-center gap-2 bg-white text-terracotta hover:bg-paperAlt rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                    >
                        <Zap className="w-4 h-4" />
                        Lancer une révision
                    </Link>
                </section>
            )}

            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats">
                <StatCard
                    icon={Sparkles}
                    label="Mots découverts"
                    value={stats.words_discovered}
                    testId="stat-discovered"
                />
                <StatCard
                    icon={Zap}
                    label="Mots actifs"
                    value={stats.words_active}
                    testId="stat-active"
                />
                <StatCard
                    icon={Target}
                    label="Mots fragiles"
                    value={stats.words_fragile}
                    testId="stat-fragile"
                    accent="warn"
                />
                <StatCard
                    icon={Flame}
                    label="Maîtrisés"
                    value={stats.words_mastered}
                    testId="stat-mastered"
                    accent="ok"
                />
                <StatCard
                    icon={Flame}
                    label="Série de jours"
                    value={stats.streak_days}
                    testId="stat-streak"
                    small
                />
                <StatCard
                    icon={Clock}
                    label="Aujourd'hui (min)"
                    value={stats.minutes_today}
                    testId="stat-minutes-today"
                    small
                />
                <StatCard
                    icon={Clock}
                    label="Total (min)"
                    value={stats.minutes_total}
                    testId="stat-minutes-total"
                    small
                />
                <StatCard
                    icon={Target}
                    label="Objectif quotidien"
                    value={`${profile?.intensity_words || 25} mots`}
                    testId="stat-goal"
                    small
                    isText
                />
            </section>

            {/* Profile card */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
                <div
                    className="lg:col-span-2 bg-white border border-lexiBorder rounded-2xl p-6 md:p-8"
                    data-testid="dashboard-profile-card"
                >
                    <p className="text-xs tracking-[0.22em] uppercase text-inkSoft mb-4">
                        Votre profil
                    </p>
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6 text-sm">
                        <ProfileLine label="Langue cible" value={
                            <span className="inline-flex items-center gap-2">
                                <span>{language?.flag}</span>
                                <span>{language?.native}</span>
                                <QualityBadge quality={language?.tts} />
                            </span>
                        } />
                        <ProfileLine label="Variante" value={profile?.variant || "—"} />
                        <ProfileLine label="Niveau" value={profile?.level} />
                        <ProfileLine label="Objectif" value={profile?.goal} />
                        <ProfileLine label="Temps/jour" value={`${profile?.daily_minutes} min`} />
                        <ProfileLine label="Intensité" value={`${profile?.intensity_words} mots/j`} />
                        <ProfileLine label="Modalité" value={profile?.modality} />
                        <ProfileLine label="Correction" value={profile?.correction} />
                        <ProfileLine
                            label="Intérêts"
                            value={(profile?.interests || []).slice(0, 4).join(", ") || "—"}
                        />
                    </dl>
                </div>
                <div className="bg-emerald text-white rounded-2xl p-6 md:p-8" data-testid="dashboard-encouragement">
                    <p className="text-[11px] tracking-[0.28em] uppercase text-white/70 mb-3">
                        Encouragement
                    </p>
                    <p className="font-serif text-lg leading-relaxed italic">
                        « Un mot entendu, vu, prononcé et utilisé devient un mot mémorisé. »
                    </p>
                    <p className="text-xs text-white/70 mt-4">
                        Vous êtes en train de bâtir un capital. Aucun jour parfait n&apos;est requis —
                        seulement des jours réguliers.
                    </p>
                </div>
            </section>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, testId, accent, small, isText }) {
    const border =
        accent === "warn" ? "border-terracotta/30" : accent === "ok" ? "border-emerald/30" : "border-lexiBorder";
    return (
        <div
            data-testid={testId}
            className={`bg-white border ${border} rounded-2xl p-5 flex flex-col gap-2 hover:-translate-y-0.5 hover:shadow-sm transition-transform`}
        >
            <Icon
                className={`w-4 h-4 ${accent === "warn" ? "text-terracotta" : accent === "ok" ? "text-emerald" : "text-inkSoft"}`}
                strokeWidth={1.75}
            />
            <div className={`font-serif leading-none ${small || isText ? "text-2xl" : "text-3xl md:text-4xl"}`}>
                {value}
            </div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-inkSoft font-medium">
                {label}
            </div>
        </div>
    );
}

function ProfileLine({ label, value }) {
    return (
        <div className="min-w-0">
            <dt className="text-[10px] tracking-[0.22em] uppercase text-inkSoft font-semibold">
                {label}
            </dt>
            <dd className="mt-1 text-ink capitalize truncate" data-testid={`profile-${label.toLowerCase()}`}>
                {value}
            </dd>
        </div>
    );
}
